// #region imports

// --- Angular
import { Component, OnDestroy, OnInit } from '@angular/core';

// --- RxJs
import { BehaviorSubject, Observable, Subscription, distinctUntilChanged, map } from 'rxjs';

// --- 3rd Party
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import _ from 'lodash';

// --- HS Code
import { Constants, GroupModel, LanguageModel, Respondent, RespondentEmailValidationModel, RespondentModel, SurveyResponseModel, SurveySummaryModel, UndoModel } from '../../../models';
import { LanguagesBaseService, ManageRespondentsService, ParticipantGroupsBaseService, RespondentsBaseService, ResponseBaseService, SurveyBaseService } from '../../../core/services';
import { BreakpointObserver } from '@angular/cdk/layout';

// #endregion

@Component({
  selector: 'hs-manage-respondents-shell-v2',
  templateUrl: './manage-respondents-shell-v2.component.html',
  styleUrls: ['./manage-respondents-shell-v2.component.scss']
})
export class ManageRespondentsShellV2Component implements OnInit, OnDestroy {

  groups$: Observable<GroupModel[]> | undefined;
  hasChanges$: Observable<boolean> | undefined;
  languages$: Observable<LanguageModel[]> | undefined;
  previousRespondent: RespondentModel | null = null;
  previousRespondents$: Observable<RespondentModel[]> | undefined;
  respondentValidationResult$: Observable<RespondentEmailValidationModel | null> | null = null;
  selectedSurvey$: Observable<SurveySummaryModel | null> | undefined;
  surveyResponse$: Observable<SurveyResponseModel | null> | undefined;

  respondents$: BehaviorSubject<RespondentModel[]>;

  isMobile$: Observable<boolean>;

  activeGroup: GroupModel | null = null;
  activeRespondent: RespondentModel = {} as RespondentModel;
  editMode: string = Constants.EDIT_MODE_NONE;
  respondentIndex: number = 0;
  viewMode: string;
  readonly viewModeManage = Constants.VIEW_MODE_MANAGE;
  readonly viewModeAdd = Constants.VIEW_MODE_ADD;
  readonly viewModeGroupAddEdit = Constants.VIEW_MODE_GROUP_ADD_EDIT;
  readonly viewModeInvitePrevious = Constants.VIEW_MODE_INVITE_PREVIOUS;
  readonly viewModeConfirmCancel = Constants.VIEW_MODE_CONFIRM_CANCEL;

  undo: UndoModel | null = null;
  private _groupsSubscription: Subscription = Subscription.EMPTY;
  private _undoSubscription: Subscription = Subscription.EMPTY;

  constructor(
    private _activeModal: NgbActiveModal,
    private _groupsService: ManageRespondentsService,
    private _languageService: LanguagesBaseService,
    private _respondentsService: RespondentsBaseService,
    private _responseService: ResponseBaseService,
    private _surveyService: SurveyBaseService,
    private _breakpointObserver: BreakpointObserver,
    ) {
    this.viewMode = this.viewModeManage;
    this.respondents$ = new BehaviorSubject<RespondentModel[]>(new Array<RespondentModel>());

    this.isMobile$ = this._breakpointObserver.observe([Constants.MOBILE_MEDIA_QUERY])
    .pipe(
      distinctUntilChanged(),
      map((state) => state.matches),
    );
  }

  ngOnInit(): void {
    document.body.classList.add('hide-intercom');

    this._groupsSubscription = this._groupsService.participantGroups$.subscribe((groups) => {
      if (!groups) {
        return;
      }

      this.respondents$.next(_.flatten(groups.map((g) => g.respondents)));
    });
    this._undoSubscription = this._groupsService.undo$.subscribe(undo => this.undo = undo);

    this.groups$ = this._groupsService.participantGroups$;
    this.hasChanges$ = this._groupsService.hasChanges$;
    this.languages$ = this._languageService.languages$;
    this.previousRespondents$ = this._respondentsService.previousRespondents$;
    this.respondentValidationResult$ = this._respondentsService.respondentValidationResult$;
    this.selectedSurvey$ = this._surveyService.selectedSelfSurvey$;
    this.surveyResponse$ = this._responseService.activeResponse$;

    this._groupsService.createSnapshot();
  }

  ngOnDestroy(): void {
    document.body.classList.remove('hide-intercom');

    if (this._undoSubscription !== Subscription.EMPTY) {
      this._undoSubscription.unsubscribe();
      this._undoSubscription = Subscription.EMPTY;
    }
    if (this._groupsSubscription !== Subscription.EMPTY) {
      this._groupsSubscription.unsubscribe();
      this._groupsSubscription = Subscription.EMPTY;
    }
  }

  onAddGroup(surveyResponse: SurveyResponseModel): void {
    this.activeGroup = this._groupsService.createGroup(surveyResponse.id, surveyResponse.numberOfRespondentsPerGroup);
    this.editMode = Constants.EDIT_MODE_ADD;
    this.viewMode = this.viewModeGroupAddEdit;
  }

  onAddPreviousClicked(respondent: RespondentModel) {
    this.previousRespondent = respondent;
  }

  onAddRespondent(addRespondentParams: any): void {
    const respondent = addRespondentParams['respondent'];
    const group = addRespondentParams['group'];
    const respondentIndex = addRespondentParams['respondentIndex'];

    this.activeRespondent = respondent;
    this.activeGroup = group;
    this.respondentIndex = respondentIndex;

    this.viewMode = this.viewModeAdd;
  }

  onBackButtonClicked(): void {
    this.viewMode = this.viewModeManage;

    this.activeRespondent = {} as RespondentModel;
    this.activeGroup = null;
    this.respondentIndex = 0;
  }

  onDeleteRespondent(respondent: RespondentModel) {
    // ToDo: Allow administrators to delete respondents who have started
    this._groupsService.deleteRespondent(respondent);
  }

  onDismissPreviousClicked(): void {
    this.previousRespondent = null;
    this.viewMode = this.viewModeAdd;
  }

  onDismissModal(dismissParams: any): void {
    const hasChanges: boolean = dismissParams['hasChanges'];
    const reason: string = dismissParams['reason'];

    if (hasChanges) {
      this.viewMode = this.viewModeConfirmCancel;
    } else {
      this._groupsService.clearSnapshot();
      this._activeModal.dismiss(reason);
    }
  }

  onConfirmAbort(reason: string): void {
    this.viewMode = this.viewModeManage;
  }

  onConfirmExit(reason: string): void {
    this._groupsService.clearSnapshot();
    this._activeModal.dismiss(reason);
  }

  onDonePreviousClicked(): void {
    if (this.previousRespondent) {
      this.activeRespondent = { //make an incomplete copy of old respondent
        ...this.previousRespondent,
        personalMessage: '', //reset old message
      };
      this.previousRespondent = null;
    };

    this.viewMode = this.viewModeAdd;
  }

  onEditGroup(group: GroupModel): void {
    this.activeGroup = group;
    this.editMode = Constants.EDIT_MODE_EDIT;
    this.viewMode = this.viewModeGroupAddEdit;
  }

  onGroupAdded(group: GroupModel): void {
    if (this.activeGroup === null) {
      this.viewMode = this.viewModeManage;
      return;
    }

    this._groupsService.addGroup(group);

    this.activeGroup = null;
    this.viewMode = this.viewModeManage;
  }

  onGroupDeleted(group: GroupModel) {
    this._groupsService.deleteGroup(group);
    this.viewMode = this.viewModeManage;
  }

  onGroupEdited(groupName: string): void {
    if (this.activeGroup === null) {
      this.viewMode = this.viewModeManage;
      return;
    }

    this.activeGroup.groupName = groupName;
    this._groupsService.updateGroup(this.activeGroup);

    this.activeGroup = null;
    this.viewMode = this.viewModeManage;
  }

  onInvitePreviousRespondent(): void {
    this.viewMode = this.viewModeInvitePrevious;
  }

  onMoveRespondent(moveRespondentParams: any): void {
    // ToDo: Allow administrators to move respondents who have started
    const respondent = moveRespondentParams['respondent'];
    const target = moveRespondentParams['targetGroup'];
    this._groupsService.moveRespondent(respondent, target);
  }

  onSaveClicked(participantId: string) {
    this._groupsService.saveChanges(participantId);
    this._activeModal.close();
  }

  onSaveRespondent(respondent: RespondentModel) {
    this._groupsService.addRespondent(respondent);

    this.viewMode = this.viewModeManage;

    this.activeRespondent = {} as RespondentModel;
    this.activeGroup = null;
    this.respondentIndex = 0;
  }

  onUndo(): void {
    this._groupsService.undo();
  }

  onUndoDismissed(): void {
    this._groupsService.clearUndo();
  }

  onValidatingRespondentEmail(model: RespondentEmailValidationModel): void {
    this._groupsService.validateRespondentEmailAddress(model);
    this._respondentsService.validateRespondentEmailAddressV2(model);
  }

  onClearValidation(): void {
    this._respondentsService.clearValidateRespondentEmailAddressV2();
  }

}
